import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  FiFileText, FiClock, FiChevronRight, FiHome,
  FiInfo, FiBookOpen, FiShield, FiDroplet, FiAlertTriangle,
} from 'react-icons/fi';
import { api } from '../api/client';

const SLUG_META = {
  about:       { Icon: FiInfo,            label: 'About Us',         gradient: ['#7c3aed', '#a855f7'] },
  terms:       { Icon: FiBookOpen,        label: 'Terms of Service', gradient: ['#2563eb', '#60a5fa'] },
  privacy:     { Icon: FiShield,          label: 'Privacy Policy',   gradient: ['#059669', '#34d399'] },
  branding:    { Icon: FiDroplet,         label: 'Branding',         gradient: ['#d946ef', '#f0abfc'] },
  'bug-bounty':{ Icon: FiAlertTriangle,   label: 'Bug Bounty',      gradient: ['#ea580c', '#fb923c'] },
};
const FALLBACK = { Icon: FiFileText, label: 'Page', gradient: ['#7c3aed', '#a855f7'] };

function StaticPage({ slug: propSlug }) {
  const { slug: paramSlug } = useParams();
  const slug = propSlug || paramSlug;
  const meta = SLUG_META[slug] || FALLBACK;
  const HeroIcon = meta.Icon;

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError('');
    api
      .get(`/v1/pages/${slug}`)
      .then((res) => setPage(res.data))
      .catch(() => setError('Page not found.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const gradientStyle = {
    '--sp-g1': meta.gradient[0],
    '--sp-g2': meta.gradient[1],
  };

  if (loading) {
    return (
      <main className="sections sp" style={gradientStyle}>
        <div className="sp-hero">
          <div className="sp-hero__glow" />
          <div className="sp-skel-icon" />
          <div className="sp-skel-bar sp-skel-bar--title" />
          <div className="sp-skel-bar sp-skel-bar--sub" />
        </div>
        <div className="sp-body">
          <div className="sp-skel-bar sp-skel-bar--full" />
          <div className="sp-skel-bar sp-skel-bar--md" />
          <div className="sp-skel-bar sp-skel-bar--full" />
          <div className="sp-skel-bar sp-skel-bar--sm" />
          <div className="sp-skel-bar sp-skel-bar--full" />
          <div className="sp-skel-bar sp-skel-bar--md" />
        </div>
      </main>
    );
  }

  if (error || !page) {
    return (
      <main className="sections sp" style={gradientStyle}>
        <div className="sp-empty">
          <div className="sp-empty__ring">
            <FiFileText />
          </div>
          <h2>Page Not Found</h2>
          <p>{error || 'The page you are looking for does not exist.'}</p>
          <Link to="/" className="sp-empty__btn">
            <FiHome />
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="sections sp" style={gradientStyle}>
      <div className="sp-hero">
        <div className="sp-hero__glow" />
        <div className="sp-hero__pattern" />

        <nav className="sp-breadcrumb">
          <Link to="/">Home</Link>
          <FiChevronRight />
          <span>{page.title}</span>
        </nav>

        <div className="sp-hero__icon-ring">
          <HeroIcon />
        </div>

        <h1 className="sp-hero__title">{page.title}</h1>

        <p className="sp-hero__meta">
          <FiClock />
          Updated recently
        </p>
      </div>

      <div className="sp-body">
        <div
          className="sp-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content) }}
        />
      </div>
    </main>
  );
}

export default StaticPage;
